import { useState, type ReactNode } from 'react';
import { COMMON } from '@locales/id/messages';
import { Button } from './Button';
import { Dialog } from './Dialog';
import { ErrorState } from './ErrorState';
import { Field } from './Field';

/**
 * A confirmed action that needs one text (a reason, a new phone number, a KK number): the field validated inline
 * (`validate` answers the sentence, or null), the send busy and single, a refusal shown inside (`onSubmit` answers
 * the sentence to show, or null when done — the caller closes the dialog then).
 */
export function PromptDialog({
  title, body, label, help, confirmLabel, danger, multiline, inputMode, maxLength, initial = '', optional = false, validate, onSubmit, onClose,
}: {
  title: string;
  body?: ReactNode;
  label: string;
  help?: string;
  confirmLabel: string;
  danger?: boolean;
  multiline?: boolean;
  inputMode?: 'text' | 'numeric' | 'tel';
  maxLength?: number;
  initial?: string;
  /** the text may stay empty (its label says so; `validate` still judges a text written) */
  optional?: boolean;
  validate: (v: string) => string | null;
  onSubmit: (v: string) => Promise<string | null>;
  onClose: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const invalid = validate(value);
  const submit = async () => {
    setTried(true);
    if (invalid || busy) return;
    setBusy(true);
    setError(null);
    try {
      setError(await onSubmit(value.trim()));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={title}
      onClose={onClose}
      dismissible={!busy}
      footer={
        <>
          <Button onClick={onClose} disabled={busy}>
            {COMMON.cancel}
          </Button>
          <Button variant={danger ? 'danger' : 'primary'} busy={busy} onClick={() => void submit()}>
            {confirmLabel}
          </Button>
        </>
      }
    >
      <form
        className="stack"
        noValidate
        onSubmit={(e) => {
          e.preventDefault();
          void submit();
        }}
      >
        {body && <div className="small">{body}</div>}
        <Field label={label} required={!optional} help={help} error={tried ? invalid : null}>
          {(id, describedBy) =>
            multiline ? (
              <textarea id={id} rows={3} maxLength={maxLength} aria-describedby={describedBy} aria-invalid={(tried && !!invalid) || undefined} value={value} onChange={(e) => setValue(e.target.value)} />
            ) : (
              <input id={id} inputMode={inputMode} maxLength={maxLength} autoComplete="off" aria-describedby={describedBy} aria-invalid={(tried && !!invalid) || undefined} value={value} onChange={(e) => setValue(e.target.value)} />
            )
          }
        </Field>
        {error && <ErrorState error={error} />}
      </form>
    </Dialog>
  );
}
